import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RoutePaths } from '../../../../core/constants/routes';
import { QuestionnaireService } from '../../../../core/services/questionnaire.service';
import { StorageService } from '../../../../core/services/storage.service';

/** Self-contained zip input + search button for new-roof-cost page only. No shared search-location. */
@Component({
  selector: 'app-new-roof-cost-zip-form',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './new-roof-cost-zip-form.component.html',
  styleUrl: './new-roof-cost-zip-form.component.scss',
})
export class NewRoofCostZipFormComponent {
  @Input() placeholder = 'Enter your zip code';
  @Input() set fetchedZipcode(value: string) {
    if (value) this.zipVal = value;
  }
  /** Optional two-way binding so multiple instances (e.g. banner + sticky) stay in sync. */
  @Input() set value(v: string) {
    if (v != null && v !== undefined) this.zipVal = v;
  }
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('zipInput') zipInputRef!: ElementRef<HTMLInputElement>;

  zipVal = '';

  constructor(
    private questionnaireService: QuestionnaireService,
    private router: Router,
    private storageService: StorageService
  ) {}

  onInput(e: Event): void {
    const input = (e.target as HTMLInputElement).value;
    this.zipVal = input.replace(/\D/g, '').slice(0, 5);
    (e.target as HTMLInputElement).value = this.zipVal;
    this.valueChange.emit(this.zipVal);
  }

  submit(): void {
    const zip = this.zipVal.replace(/\D/g, '').trim();
    if (zip.length < 5) {
      this.zipInputRef?.nativeElement?.focus();
      return;
    }
    this.questionnaireService.updateQuestionaryStep({ zipCode: zip });
    this.storageService.setItem('originLeads', 'true');
    const currentRoute = this.router.url.split('?')[0];
    const landingPath =
      currentRoute === '/' || currentRoute === ''
        ? RoutePaths.HOME
        : currentRoute.replace(/^\//, '');
    this.storageService.setItem('landing-page-path', landingPath);
    this.router.navigate([`/${RoutePaths.QUESTIONNAIRE}`]);
  }

  onEnter(e: Event): void {
    e.preventDefault();
    this.submit();
  }

  onInputFocus(): void {
    this.moveCaretToEnd();
  }

  onInputClick(): void {
    this.moveCaretToEnd();
  }

  /** Expose for parent (e.g. step 1 CTA). */
  get value(): string {
    return this.zipVal;
  }

  focus(): void {
    const el = this.zipInputRef?.nativeElement;
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      el.focus();
      this.moveCaretToEnd();
    }
  }

  private moveCaretToEnd(): void {
    const input = this.zipInputRef?.nativeElement;
    if (!input) return;

    const end = input.value.length;
    try {
      input.setSelectionRange(end, end);
    } catch {
      // Ignore browsers/input types that do not support selection APIs.
    }

    // iOS Safari may reset the caret after focus/click; schedule one more pass.
    setTimeout(() => {
      try {
        input.setSelectionRange(end, end);
      } catch {
        // Ignore browsers/input types that do not support selection APIs.
      }
    }, 0);
  }
}
